import { Pressable, ScrollView, View } from "@-/com.npmjs.react-native";
import { cn } from "@-/com.npmjs.cn";
import { format } from "@-/com.npmjs.date-fns";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Switch } from "@-/dev.improve.haniverse.components.ui.switch";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  formatHan,
  formatHanMiles,
  formatHanMilesAmount,
  HAN_MILES_PER_KM,
  type HanWallet,
} from "@-/dev.improve.haniverse.lib.han-currency";
import {
  formatTravelDuration,
  formatYuan,
  SEAT_CLASS_LABELS,
  TRANSPORT_MODE_ICONS,
  TRANSPORT_MODE_LABELS,
  travelOptionHanMilesRedemption,
  type TravelOption,
  type TravelState,
} from "@-/dev.improve.haniverse.lib.travel";
import { useState, type ReactNode } from "react";
import type { ChinaMapPlaceView } from "#china-map-places";

function ActionButton({
  children,
  disabled = false,
  onPress,
  testID,
  tone = "primary",
}: {
  children: ReactNode;
  disabled?: boolean;
  onPress: () => void;
  testID: string;
  tone?: "danger" | "primary" | "secondary";
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      className={cn(
        "flex-row items-center justify-center gap-1.5 rounded-full px-4 py-2",
        tone === "primary" && "bg-foreground active:opacity-80",
        tone === "secondary" && "border border-border bg-background active:bg-accent",
        tone === "danger" && "border border-red-600 bg-background active:bg-accent",
        disabled && "opacity-40",
      )}
      disabled={disabled}
      onPress={onPress}
      testID={testID}
    >
      {children}
    </Pressable>
  );
}

function ButtonLabel({
  children,
  tone = "primary",
}: {
  children: ReactNode;
  tone?: "danger" | "primary" | "secondary";
}) {
  return (
    <Text
      className={cn(
        "text-sm font-semibold",
        tone === "primary" && "text-background",
        tone === "secondary" && "text-foreground",
        tone === "danger" && "text-red-600",
      )}
    >
      {children}
    </Text>
  );
}

/** A stored trip's mode is free text; unknown modes (from a newer app version) get a compass. */
function tripModeIcon(mode: string): string {
  return Object.entries(TRANSPORT_MODE_ICONS).find(([key]) => key === mode)?.[1] ?? "🧭";
}

function formatArrival(arrivesAt: string, now: Date): string {
  const arrival = new Date(arrivesAt);
  const sameDay = arrival.toDateString() === now.toDateString();
  return format(arrival, sameDay ? "HH:mm" : "EEE HH:mm");
}

/**
 * The travel row under a selected place: start here, you are here, teleport, travel, ride a
 * dragon — or why none apply yet.
 */
export function ChinaMapPlaceTravelActions({
  cheapestCostHan,
  isBusy,
  nameOf,
  onRideDragon,
  onShowOptions,
  onStartHere,
  onTeleport,
  placeNounSingular,
  state,
  view,
  wallet,
}: {
  cheapestCostHan: number | null;
  isBusy: boolean;
  nameOf: (adcode: number) => string;
  onRideDragon: () => void;
  onShowOptions: () => void;
  onStartHere: () => void;
  onTeleport: () => void;
  /** What the place's province's places are called, e.g. "city". */
  placeNounSingular: string;
  state: TravelState;
  view: ChinaMapPlaceView;
  wallet: HanWallet;
}) {
  const adcode = view.place.adcode;
  // Journeys run between cities and districts: a province with cities is only a way to zoom in.
  if (view.place === view.province && view.province.children.length > 0) {
    return (
      <Text className="text-sm text-muted-foreground">
        Tap a {placeNounSingular} in {view.displayName} to travel there.
      </Text>
    );
  }
  const unlocked = view.unlock.status === "unlocked";
  const visited = state.visited.has(adcode);
  if (state.kind === "unplaced") {
    return unlocked ? (
      <ActionButton disabled={isBusy} onPress={onStartHere} testID="china-map-start-here">
        <ButtonLabel>📍 Start my journey here</ButtonLabel>
      </ActionButton>
    ) : (
      <Text className="text-sm text-muted-foreground">
        Learn every character of {view.displayName} to start here.
      </Text>
    );
  }
  if (state.kind === "in-transit") {
    return (
      <Text className="text-sm text-muted-foreground">
        {tripModeIcon(state.trip.mode)} On the way to {nameOf(state.trip.destinationAdcode)} ·
        arrives {formatArrival(state.trip.arrivesAt, new Date())}
      </Text>
    );
  }
  if (state.adcode === adcode) {
    return (
      <Text className="text-sm font-semibold text-red-600" testID="china-map-you-are-here">
        📍 You are here
      </Text>
    );
  }
  if (visited) {
    return (
      <ActionButton disabled={isBusy} onPress={onTeleport} testID="china-map-teleport">
        <ButtonLabel>✨ Teleport here (free)</ButtonLabel>
      </ActionButton>
    );
  }
  if (!unlocked) {
    return (
      <Text className="text-sm text-muted-foreground">
        🔒 Learn every character of {view.displayName} to travel here.
      </Text>
    );
  }
  return (
    <View className="flex-row flex-wrap gap-2">
      <ActionButton
        disabled={isBusy || cheapestCostHan === null}
        onPress={onShowOptions}
        testID="china-map-travel"
      >
        <ButtonLabel>
          {cheapestCostHan === null ? "No route" : `Travel · from ${formatHan(cheapestCostHan)}`}
        </ButtonLabel>
      </ActionButton>
      {wallet.dragons > 0 ? (
        <ActionButton
          disabled={isBusy}
          onPress={onRideDragon}
          testID="china-map-ride-dragon"
          tone="secondary"
        >
          <ButtonLabel tone="secondary">龙 Ride a dragon</ButtonLabel>
        </ActionButton>
      ) : null}
    </View>
  );
}

function legRoute(option: TravelOption, nameOf: (key: number) => string): string {
  const stops = [option.legs[0]?.from, ...option.legs.map((leg) => leg.to)].filter(
    (stop) => stop !== undefined,
  );
  return stops.map((stop) => nameOf(stop)).join(" → ");
}

function optionTitle(option: TravelOption): string {
  return `${TRANSPORT_MODE_LABELS[option.mode]} · ${SEAT_CLASS_LABELS[option.seatClass]}`;
}

/**
 * The ways to get from here to the selected place, with their real fares and journey times. A
 * journey with a flight can spend HanMiles on a discount: an option is affordable when the Han left
 * after the largest discount covers it, and choosing one turns the discount on.
 */
export function ChinaMapTravelOptionsCard({
  destination,
  isBusy,
  nameOf,
  onBack,
  onPurchase,
  options,
  originName,
  wallet,
}: {
  destination: ChinaMapPlaceView;
  isBusy: boolean;
  nameOf: (key: number) => string;
  onBack: () => void;
  onPurchase: (option: TravelOption, redeemHanMiles: boolean) => void;
  options: readonly TravelOption[];
  originName: string;
  wallet: HanWallet;
}) {
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [redeemHanMiles, setRedeemHanMiles] = useState(true);
  const chosen = options.find((option) => option.id === chosenId) ?? null;
  const chosenRedemption =
    chosen === null ? null : travelOptionHanMilesRedemption(chosen, wallet.hanMiles);
  const canRedeem = chosenRedemption !== null && chosenRedemption.discountHan > 0;
  const chosenCostHan =
    chosen === null
      ? 0
      : chosen.costHan - (canRedeem && redeemHanMiles ? chosenRedemption.discountHan : 0);
  return (
    <View className="flex-1 gap-2 px-4 py-3" testID="china-map-travel-options">
      <View className="flex-row items-center gap-3">
        <Pressable
          accessibilityLabel="Back"
          accessibilityRole="button"
          className="size-8 items-center justify-center rounded-full bg-muted active:bg-accent"
          hitSlop={8}
          onPress={onBack}
          testID="china-map-travel-options-back"
        >
          <Icon className="text-foreground" name="MdChevronLeft" size={18} />
        </Pressable>
        <Text className="flex-1 text-lg font-semibold text-foreground" numberOfLines={1}>
          {originName} → {destination.displayName}
        </Text>
        <View className="items-end">
          <Text className="text-sm text-muted-foreground">{formatHan(wallet.han)}</Text>
          {wallet.hanMiles > 0 ? (
            <View className="flex-row items-center gap-0.5">
              <Icon className="text-muted-foreground" name="MdFlightTakeoff" size={12} />
              <Text className="text-xs text-muted-foreground tabular-nums">
                {formatHanMilesAmount(wallet.hanMiles)}
              </Text>
            </View>
          ) : null}
        </View>
      </View>
      {chosen === null ? (
        <ScrollView contentContainerClassName="gap-1.5" showsVerticalScrollIndicator={false}>
          {options.map((option, index) => {
            const { discountHan } = travelOptionHanMilesRedemption(option, wallet.hanMiles);
            const lowestCostHan = option.costHan - discountHan;
            const affordable = wallet.han >= lowestCostHan;
            return (
              <Pressable
                accessibilityLabel={`${optionTitle(option)}, ${formatTravelDuration(option.durationMinutes)}, ${option.costHan} Han`}
                accessibilityRole="button"
                accessibilityState={{ disabled: !affordable }}
                className={cn(
                  "flex-row items-center gap-3 rounded-xs border border-border px-3 py-2 active:bg-accent",
                  !affordable && "opacity-50",
                )}
                disabled={!affordable || isBusy}
                key={option.id}
                onPress={() => {
                  setChosenId(option.id);
                  setRedeemHanMiles(true);
                }}
                testID={`travel-option-${index}`}
              >
                <Text className="text-2xl">{TRANSPORT_MODE_ICONS[option.mode]}</Text>
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-foreground" numberOfLines={1}>
                    {optionTitle(option)}
                    {option.source === "timetable" ? "" : " · est."}
                  </Text>
                  <Text className="text-xs text-muted-foreground" numberOfLines={1}>
                    {formatTravelDuration(option.durationMinutes)} · {legRoute(option, nameOf)}
                  </Text>
                </View>
                <View className="items-end">
                  <Text className="text-sm font-semibold text-foreground">
                    {formatHan(option.costHan)}
                  </Text>
                  {affordable && discountHan > 0 ? (
                    <Text
                      className="text-xs font-semibold text-red-600"
                      testID={`travel-option-${index}-han-miles-discount`}
                    >
                      ✈ −{formatHan(discountHan)}
                    </Text>
                  ) : (
                    <Text className="text-xs text-muted-foreground">
                      {affordable
                        ? formatYuan(option.fareCny)
                        : `need ${formatHan(lowestCostHan - wallet.han)}`}
                    </Text>
                  )}
                </View>
              </Pressable>
            );
          })}
        </ScrollView>
      ) : (
        <View className="flex-1 gap-2">
          {/* The card has a fixed height: the details scroll, the choice and buttons stay put. */}
          <ScrollView
            className="flex-1"
            contentContainerClassName="gap-1"
            showsVerticalScrollIndicator={false}
          >
            <Text className="text-base font-semibold text-foreground">
              {TRANSPORT_MODE_ICONS[chosen.mode]} {optionTitle(chosen)}
            </Text>
            <Text className="text-sm text-muted-foreground">
              {legRoute(chosen, nameOf)} · {formatTravelDuration(chosen.durationMinutes)} of real
              travel time. You arrive when it has passed.
            </Text>
            <Text className="text-xs text-muted-foreground" numberOfLines={1}>
              {chosen.legs
                .map((leg) =>
                  leg.example === null
                    ? `${TRANSPORT_MODE_LABELS[leg.mode]} ${formatYuan(leg.fareCny)}`
                    : `${leg.example} ${formatYuan(leg.fareCny)}`,
                )
                .join(" + ")}
            </Text>
            <Text className="text-xs text-muted-foreground" testID="travel-option-han-miles-earned">
              Earns {formatHanMiles(chosen.distanceKm * HAN_MILES_PER_KM)} when you set off.
            </Text>
          </ScrollView>
          {canRedeem ? (
            <Pressable
              accessibilityLabel={`Use ${chosenRedemption.hanMilesRedeemed} HanMiles for ${chosenRedemption.discountHan} Han off`}
              accessibilityRole="switch"
              accessibilityState={{ checked: redeemHanMiles }}
              className="flex-row items-center gap-2 rounded-xs border border-border px-3 py-1.5 active:bg-accent"
              onPress={() => setRedeemHanMiles((value) => !value)}
              testID="travel-option-redeem-han-miles"
            >
              <Icon className="text-foreground" name="MdFlightTakeoff" size={16} />
              <Text className="flex-1 text-sm text-foreground" numberOfLines={1}>
                <Text className="font-semibold">
                  Use {formatHanMiles(chosenRedemption.hanMilesRedeemed)}
                </Text>
                <Text className="text-muted-foreground">
                  {" "}
                  · −{formatHan(chosenRedemption.discountHan)}
                </Text>
              </Text>
              <Switch
                accessible={false}
                checked={redeemHanMiles}
                importantForAccessibility="no-hide-descendants"
                onCheckedChange={setRedeemHanMiles}
                pointerEvents="none"
              />
            </Pressable>
          ) : null}
          <View className="flex-row gap-2">
            <ActionButton
              onPress={() => setChosenId(null)}
              testID="travel-option-cancel"
              tone="secondary"
            >
              <ButtonLabel tone="secondary">Back</ButtonLabel>
            </ActionButton>
            <ActionButton
              disabled={isBusy || wallet.han < chosenCostHan}
              onPress={() => onPurchase(chosen, canRedeem && redeemHanMiles)}
              testID="travel-option-confirm"
            >
              <ButtonLabel>
                {wallet.han < chosenCostHan
                  ? `Need ${formatHan(chosenCostHan - wallet.han)} more`
                  : `Pay ${formatHan(chosenCostHan)} and depart`}
              </ButtonLabel>
            </ActionButton>
          </View>
        </View>
      )}
    </View>
  );
}

/** Replaces the overview while the learner is on the road, with a live countdown. */
export function ChinaMapInTransitCard({
  isBusy,
  nameOf,
  onCancel,
  quests,
  state,
}: {
  isBusy: boolean;
  nameOf: (adcode: number) => string;
  onCancel: () => void;
  /** The quests offered on the road: the global ones. */
  quests: ReactNode;
  state: Extract<TravelState, { kind: "in-transit" }>;
}) {
  const [confirming, setConfirming] = useState(false);
  const { trip } = state;
  const now = new Date();
  const remainingMinutes = (Date.parse(trip.arrivesAt) - now.getTime()) / 60_000;
  const icon = tripModeIcon(trip.mode);
  return (
    <View className="flex-1 gap-2 px-4 py-3" testID="china-map-in-transit-card">
      <Text className="text-lg font-semibold text-foreground" numberOfLines={1}>
        {icon} {nameOf(trip.originAdcode)} → {nameOf(trip.destinationAdcode)}
      </Text>
      <View className="h-2 overflow-hidden rounded-full bg-muted">
        <View
          className="h-full rounded-full bg-red-600"
          style={{ width: `${Math.round(state.progress * 100)}%` }}
        />
      </View>
      <Text className="text-sm text-muted-foreground" testID="china-map-in-transit-remaining">
        Arrives in {formatTravelDuration(Math.ceil(remainingMinutes))} (at{" "}
        {formatArrival(trip.arrivesAt, now)}) · paid {formatHan(trip.costHan)}
        {trip.hanMilesRedeemed > 0 ? ` and ${formatHanMiles(trip.hanMilesRedeemed)}` : ""}
      </Text>
      {confirming ? (
        <View className="flex-row flex-wrap items-center gap-2">
          <Text className="text-sm text-foreground">
            Turn back? The fare is not refunded, and the journey earns no HanMiles.
          </Text>
          <ActionButton
            onPress={() => setConfirming(false)}
            testID="china-map-cancel-trip-keep"
            tone="secondary"
          >
            <ButtonLabel tone="secondary">Keep going</ButtonLabel>
          </ActionButton>
          <ActionButton
            disabled={isBusy}
            onPress={onCancel}
            testID="china-map-cancel-trip-confirm"
            tone="danger"
          >
            <ButtonLabel tone="danger">Turn back</ButtonLabel>
          </ActionButton>
        </View>
      ) : (
        <View className="flex-row">
          <ActionButton
            onPress={() => setConfirming(true)}
            testID="china-map-cancel-trip"
            tone="danger"
          >
            <ButtonLabel tone="danger">Cancel trip</ButtonLabel>
          </ActionButton>
        </View>
      )}
      <View className="pt-3">{quests}</View>
    </View>
  );
}

/**
 * Quick picks under the overview: where to start (before the first trip), or where to go next.
 * Places are chosen on the map too; these make the common choices one tap away.
 */
export function ChinaMapPlaceChips({
  onSelect,
  testIdPrefix,
  views,
}: {
  onSelect: (view: ChinaMapPlaceView) => void;
  testIdPrefix: string;
  views: readonly ChinaMapPlaceView[];
}) {
  return (
    <ScrollView
      contentContainerClassName="items-center gap-2"
      horizontal
      showsHorizontalScrollIndicator={false}
    >
      {views.map((view) => (
        <Pressable
          accessibilityLabel={view.displayName}
          accessibilityRole="button"
          className="rounded-full border border-border px-3 py-1 active:bg-accent"
          key={view.place.adcode}
          onPress={() => onSelect(view)}
          testID={`${testIdPrefix}-${view.place.adcode}`}
        >
          <Text className="text-base text-foreground">{view.displayName}</Text>
        </Pressable>
      ))}
    </ScrollView>
  );
}
